import { create } from 'zustand';
import { 
  getContainers, 
  createContainer, 
  updateContainer, 
  deleteContainer,
  getContainer
} from '../services/container.service';
import { 
  ContainerSchema, 
  ContainerRequest, 
  UpdateContainerRequest, 
  ContainerQueryParams,
  PaginatedContainerResponse 
} from '../types/container.type';
import { AxiosError } from 'axios';

interface ContainerState {
  containers: ContainerSchema[];
  selectedContainer: ContainerSchema | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  showContainerModal: boolean;
  lastQueryParams: ContainerQueryParams | null;
  setShowContainerModal: (show: boolean) => void;
  getContainers: (params: ContainerQueryParams) => Promise<PaginatedContainerResponse | null>;
  getContainer: (containerId: string) => Promise<ContainerSchema | null>;
  createContainer: (container: ContainerRequest) => Promise<ContainerSchema | null>;
  updateContainer: (containerId: string, container: UpdateContainerRequest) => Promise<ContainerSchema | null>;
  deleteContainer: (containerId: string) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useContainerStore = create<ContainerState>((set, get) => ({
  containers: [],
  selectedContainer: null,
  total: 0,
  isLoading: false,
  error: null,
  showContainerModal: false,
  lastQueryParams: null,
  setShowContainerModal: (show: boolean) => set({ showContainerModal: show }),

  getContainers: async (params) => {
    set({ isLoading: true, error: null, lastQueryParams: params });
    try {
      const response = await getContainers(params);
      const data = response as PaginatedContainerResponse;
      
      set({ 
        containers: data.containers,
        total: data.total,
        isLoading: false 
      });

      return {
        containers: data.containers,
        total: data.total
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get containers', isLoading: false });
      } else {
        set({ error: 'Failed to get containers', isLoading: false });
      }
      return null;
    }
  },

  getContainer: async (containerId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getContainer(containerId);
      const data = response as GenericResponse<ContainerSchema>;
      
      set({ 
        selectedContainer: data.result,
        isLoading: false 
      });

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get container', isLoading: false });
      } else {
        set({ error: 'Failed to get container', isLoading: false });
      }
      return null;
    }
  },

  createContainer: async (container) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createContainer(container);
      const data = response as GenericResponse<ContainerSchema>;
      
      // Refetch containers list to get the full data including populated fields
      const lastParams = get().lastQueryParams;
      if (lastParams) {
        await get().getContainers(lastParams);
      } else {
        // Fallback: add to local state if no query params
        set((state) => ({ 
          containers: [...state.containers, data.result],
          isLoading: false 
        }));
      }

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to create container', isLoading: false });
      } else {
        set({ error: 'Failed to create container', isLoading: false });
      }
      return null;
    }
  },

  updateContainer: async (containerId, container) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateContainer(containerId, container);
      const data = response as GenericResponse<ContainerSchema>;
      
      // Refetch containers list to get the full data including populated fields
      const lastParams = get().lastQueryParams;
      if (lastParams) {
        await get().getContainers(lastParams);
      } else {
        // Fallback: update local state if no query params
        set((state) => ({ 
          containers: state.containers.map(c => c.id === containerId ? data.result : c),
          selectedContainer: state.selectedContainer?.id === containerId ? data.result : state.selectedContainer,
          isLoading: false 
        }));
      }

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to update container', isLoading: false });
      } else {
        set({ error: 'Failed to update container', isLoading: false });
      }
      return null;
    }
  },

  deleteContainer: async (containerId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteContainer(containerId);
      
      set((state) => ({ 
        containers: state.containers.filter(c => c.id !== containerId),
        selectedContainer: state.selectedContainer?.id === containerId ? null : state.selectedContainer,
        isLoading: false 
      }));

      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to delete container', isLoading: false });
      } else {
        set({ error: 'Failed to delete container', isLoading: false });
      }
      return false;
    }
  },
})); 